import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, EllipsisVerticalIcon, GlobeAltIcon, LinkIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/api';

function fmt(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString();
}

function percent(num, den) {
  if (!den) return 0;
  return Math.min(100, Math.round((Number(num) / Number(den)) * 100));
}

/** Slim polls freeze recount fields — prefer live $inc ingest counters. */
function liveRawUnique(stats) {
  return Math.max(Number(stats?.rawUnique) || 0, Number(stats?.ingest?.netNewRaw) || 0);
}

function liveRowsFetched(stats, sourceProgress) {
  const fromSources = (sourceProgress || []).length
    ? sumCrawlableProgress(sourceProgress).rowsFetched
    : 0;
  const fromCache = Number(stats?.rowsFetchedEstimate) || 0;
  const fromIngest = (Number(stats?.ingest?.skippedSeenRaw) || 0)
    + (Number(stats?.ingest?.netNewRaw) || 0);
  return Math.max(fromSources, fromCache, fromIngest);
}

function liveFilteredReady(stats) {
  return Math.max(
    Number(stats?.filteredReady) || 0,
    Number(stats?.byCheckState?.published) || 0
  );
}

/**
 * Has this source stopped crawling? `partial` has finished its pass.
 * `exhausted_dup` is leftover from the old duplicate-page stop — those
 * sources still have pages left and belong in Running until API end.
 * Mirrors isSourceCrawlFinished in backend/utils/scrapeV2Constants.js.
 */
export function isSourceCrawlFinished(status, row) {
  if (row?.excluded === true && row?.domainOverride !== true) return true;
  return status === 'completed'
    || status === 'suspended'
    || status === 'partial'
    || status === 'excluded';
}

export function isDomainBlocked(src) {
  return src?.excluded === true && src?.domainOverride !== true;
}

/**
 * Source progress buckets. Domain-blocked rows are never mixed into
 * running or finished — they have their own tab.
 */
export function sourceProgressBucket(src) {
  if (isDomainBlocked(src)) return 'blocked';
  const status = src?.status;
  if (isSourceCrawlFinished(status, src) || status === 'paused' || status === 'stopped') {
    return 'finished';
  }
  return 'running';
}

export function partitionSourceProgress(sourceProgress) {
  const running = [];
  const finished = [];
  const blocked = [];
  for (const src of sourceProgress || []) {
    const bucket = sourceProgressBucket(src);
    if (bucket === 'blocked') blocked.push(src);
    else if (bucket === 'finished') finished.push(src);
    else running.push(src);
  }
  return { running, finished, blocked };
}

export function countFinishedSources(sourceProgress) {
  return partitionSourceProgress(sourceProgress).finished.length;
}

/** X's Followers list ended — fetched rows are the crawl target, not the profile badge. */
export function isApiEndSource(src) {
  if (src?.exitReason === 'api_end') return true;
  // Older statsCache rows are `completed` with exitReason omitted.
  return src?.status === 'completed' && !src?.exitReason;
}

/** Profile badge is an estimate. `api_end` means the list X gave us is complete. */
export function crawlTargetForSource(src) {
  const fetched = Number(src?.rowsFetched ?? src?.scrapedCount) || 0;
  const badge = Number(src?.followersCount) || 0;
  if (isApiEndSource(src) && fetched > 0) return fetched;
  return badge;
}

/** Always recompute — never trust a stale crawlPercent from statsCache. */
export function sourceCrawlStats(src) {
  const fetched = Number(src?.rowsFetched ?? src?.scrapedCount) || 0;
  const ingested = Number(src?.rawIngested) || 0;
  const target = crawlTargetForSource(src);
  return {
    fetched,
    ingested,
    target,
    crawlPercent: target > 0 ? percent(fetched, target) : null,
    ingestPercent: target > 0 ? percent(ingested, target) : null,
  };
}

/** Rows fetched + follower target for running + finished only (not domain-blocked). */
export function sumCrawlableProgress(sourceProgress) {
  const { running, finished } = partitionSourceProgress(sourceProgress);
  let rowsFetched = 0;
  let totalFollowers = 0;
  for (const src of running.concat(finished)) {
    const fetched = Number(src?.rowsFetched ?? src?.scrapedCount) || 0;
    rowsFetched += fetched;
    totalFollowers += crawlTargetForSource({ ...src, rowsFetched: fetched });
  }
  return { rowsFetched, totalFollowers };
}

function StageCard({ stepNumber, title, subtitle, accent = 'slate', bar, stats, footer }) {
  const accents = {
    blue: 'from-blue-50/80 to-white/60 dark:from-blue-900/20 dark:to-slate-900/30 border-blue-200/60 dark:border-blue-800/60',
    indigo: 'from-indigo-50/80 to-white/60 dark:from-indigo-900/20 dark:to-slate-900/30 border-indigo-200/60 dark:border-indigo-800/60',
    violet: 'from-violet-50/80 to-white/60 dark:from-violet-900/20 dark:to-slate-900/30 border-violet-200/60 dark:border-violet-800/60',
    emerald: 'from-emerald-50/80 to-white/60 dark:from-emerald-900/20 dark:to-slate-900/30 border-emerald-200/60 dark:border-emerald-800/60',
    slate: 'from-slate-50/80 to-white/60 dark:from-slate-800/40 dark:to-slate-900/30 border-slate-200/60 dark:border-slate-700/60',
  };
  const badge = {
    blue: 'bg-blue-500/90',
    indigo: 'bg-indigo-500/90',
    violet: 'bg-violet-500/90',
    emerald: 'bg-emerald-500/90',
    slate: 'bg-slate-500/90',
  };
  const toneClass = {
    slate: 'text-slate-700 dark:text-slate-200',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
    blue: 'text-blue-600 dark:text-blue-400',
    violet: 'text-violet-600 dark:text-violet-400',
    orange: 'text-orange-600 dark:text-orange-400',
    red: 'text-red-600 dark:text-red-400',
    gray: 'text-slate-500 dark:text-slate-400',
  };

  return (
    <div className={`relative rounded-2xl p-3.5 border backdrop-blur-xl bg-gradient-to-br shadow-sm ${accents[accent] || accents.slate}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`flex items-center justify-center h-5 w-5 rounded-full text-white text-[10px] font-bold shadow ${badge[accent] || badge.slate}`}>
            {stepNumber}
          </span>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{title}</div>
            {subtitle && <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{subtitle}</div>}
          </div>
        </div>
      </div>
      {bar && (
        <div className="mb-2.5">
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="text-slate-500 dark:text-slate-400">{bar.label}</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">{bar.percent}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/60 dark:bg-slate-800/70 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${bar.barColor}`} style={{ width: `${bar.percent}%` }} />
          </div>
        </div>
      )}
      {stats?.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{s.label}</span>
              <span className={`text-sm font-semibold tabular-nums ${toneClass[s.tone || 'slate']}`}>{fmt(s.value)}</span>
            </div>
          ))}
        </div>
      )}
      {footer && (
        <div className="mt-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60 text-[10px] text-slate-500 dark:text-slate-400">
          {footer}
        </div>
      )}
    </div>
  );
}

export function ScraperV2OverallProgress({
  stats,
  sourceProgress,
  onBackfillFollowers,
  backfill,
  backfillBusy = false,
}) {
  const rawUnique = liveRawUnique(stats);
  const { running: runningSources, finished: finishedSources, blocked: blockedSources } =
    partitionSourceProgress(sourceProgress);
  const crawlable = (sourceProgress || []).length
    ? sumCrawlableProgress(sourceProgress)
    : {
        rowsFetched: liveRowsFetched(stats, sourceProgress),
        totalFollowers: Number(stats?.totalFollowersEstimate) || 0,
      };
  const rowsFetched = Math.max(crawlable.rowsFetched, liveRowsFetched(stats, sourceProgress));
  const totalFollowers = crawlable.totalFollowers;
  const completed = (sourceProgress || []).length
    ? finishedSources.length
    : Number(stats?.sourcesComplete) || 0;
  const crawlableSources = (sourceProgress || []).length
    ? runningSources.length + finishedSources.length
    : Number(stats?.sourcesTotal) || 0;
  const totalSources = (sourceProgress || []).length || Number(stats?.sourcesTotal) || 0;
  const overallPct = percent(rowsFetched, totalFollowers);

  // Without a profile follower total the source has no denominator, so it
  // shows "fetched / ?" and contributes nothing to overall progress.
  const missingTotals = (sourceProgress || []).filter(
    (s) => !isDomainBlocked(s) && !(Number(s.followersCount) > 0)
  ).length;

  // A driver killed mid-repair can leave the status stuck at "running"; after
  // this long, let the operator retry rather than disabling the button forever.
  const STALE_RUNNING_MS = 15 * 60 * 1000;
  const startedAt = backfill?.startedAt ? new Date(backfill.startedAt).getTime() : 0;
  const running = backfill?.status === 'running'
    && (!startedAt || Date.now() - startedAt < STALE_RUNNING_MS);

  // Keep the panel mounted while a repair runs even once the last total lands,
  // so the operator sees it through to completion.
  const canRepair = Boolean(onBackfillFollowers) && (missingTotals > 0 || running);

  return (
    <div className="mb-4 rounded-2xl p-3.5 border border-slate-200/60 dark:border-slate-700/60 bg-slate-50/70 dark:bg-slate-800/40 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">Overall progress</span>
        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 tabular-nums">
          {overallPct}% ·{' '}
          <span className="text-slate-500 dark:text-slate-400">
            {fmt(rowsFetched)} / {totalFollowers > 0 ? fmt(totalFollowers) : '—'}
          </span>
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-white/70 dark:bg-slate-900/50 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
          style={{ width: `${overallPct}%` }}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
        <span>
          {completed} / {crawlableSources || totalSources} sources finished
          {blockedSources.length > 0 ? ` · ${fmt(blockedSources.length)} blocked` : ''}
        </span>
        <span>{fmt(rawUnique)} raw stored this session</span>
      </div>
      {canRepair && (
        <div className="mt-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[11px] text-amber-600 dark:text-amber-500">
            {running
              ? missingTotals > 0
                ? `Looking up follower totals — ${fmt(missingTotals)} source(s) to go…`
                : 'Finishing up…'
              : backfill?.status === 'aborted'
                ? `Paused on rate limit — crawl will fill remaining totals`
              : backfill?.status === 'failed'
                ? `Last attempt failed${backfill.error ? `: ${backfill.error}` : ''}`
                : `${fmt(missingTotals)} source(s) have no follower total — progress can't show a target`}
          </span>
          <button
            type="button"
            disabled={backfillBusy || running}
            onClick={onBackfillFollowers}
            className="px-2.5 py-1 rounded-lg text-[11px] font-medium border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50 transition-colors"
          >
            {running || backfillBusy ? 'Fixing…' : 'Fix follower totals'}
          </button>
        </div>
      )}
      {backfill?.status === 'done' && missingTotals === 0 && Number(backfill.filled) > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60 text-[11px] text-emerald-600 dark:text-emerald-500">
          Filled {fmt(backfill.filled)} follower total(s).
        </div>
      )}
    </div>
  );
}

export function ScraperV2PipelineStages({ stats, sourceProgress, filters = {} }) {
  const countryFiltering = Boolean(filters.filterCountryWhitelist);
  const activityFiltering = Boolean(filters.filterActiveLastMonth);

  const crawlable = (sourceProgress || []).length
    ? sumCrawlableProgress(sourceProgress)
    : {
        rowsFetched: liveRowsFetched(stats, sourceProgress),
        totalFollowers: Number(stats?.totalFollowersEstimate) || 0,
      };
  const rowsFetched = Math.max(crawlable.rowsFetched, liveRowsFetched(stats, sourceProgress));
  const rawUnique = liveRawUnique(stats);
  const duplicates = Math.max(0, rowsFetched - rawUnique);
  const skippedGlobal = Number(stats?.ingest?.skippedSeenRaw) || 0;
  const filteredReady = liveFilteredReady(stats);
  const totalFollowers = crawlable.totalFollowers;

  const country = stats?.pipeline?.country || {};
  const activity = stats?.pipeline?.activity || {};
  const byCheck = stats?.byCheckState || {};

  const gridCls = 'grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-4';

  let step = 1;

  return (
    <div className={gridCls}>
      <StageCard
        stepNumber={step++}
        title="Fetch"
        subtitle="Raw rows from X"
        accent="blue"
        bar={{
          label: 'Scrape progress',
          percent: percent(rowsFetched, totalFollowers),
          barColor: 'bg-gradient-to-r from-blue-400 to-blue-500',
        }}
        stats={[
          { label: 'Fetched', value: rowsFetched, tone: 'blue' },
          { label: 'Raw stored', value: rawUnique, tone: 'emerald' },
          { label: 'Duplicates', value: duplicates, tone: 'orange' },
          ...(skippedGlobal > 0
            ? [{ label: 'Skipped (global)', value: skippedGlobal, tone: 'gray' }]
            : []),
        ]}
        footer={duplicates > 0
          ? 'Repeat sightings or blacklist drops — not extra people on this follower list'
          : (skippedGlobal > 0
            ? 'Duplicates = fetched − raw stored (global skips counted in fetched)'
            : null)}
      />
      <StageCard
        stepNumber={step++}
        title="Country"
        subtitle={countryFiltering ? 'Whitelist filter' : 'Collect signals'}
        accent="indigo"
        bar={{
          label: countryFiltering ? 'Pass rate' : 'Checked',
          percent: percent(country.passed, country.checked),
          barColor: 'bg-gradient-to-r from-indigo-400 to-indigo-500',
        }}
        stats={[
          { label: 'Checked', value: country.checked, tone: 'indigo' },
          { label: countryFiltering ? 'Match' : 'Kept', value: country.passed, tone: 'emerald' },
          { label: 'Not in list', value: country.rejected, tone: 'gray' },
          { label: 'Pending', value: country.pending, tone: 'orange' },
        ]}
        footer={byCheck.checking_country ? `${fmt(byCheck.checking_country)} checking now` : null}
      />
      <StageCard
        stepNumber={step++}
        title="Activity"
        subtitle={activityFiltering ? 'Active-only filter' : 'Collect signals'}
        accent="violet"
        bar={{
          label: activityFiltering ? 'Pass rate' : 'Checked',
          percent: percent(activity.passed, activity.checked),
          barColor: 'bg-gradient-to-r from-violet-400 to-violet-500',
        }}
        stats={[
          { label: 'Checked', value: activity.checked, tone: 'violet' },
          { label: activityFiltering ? 'Active' : 'Kept', value: activity.passed, tone: 'emerald' },
          { label: 'Inactive', value: activity.inactive, tone: 'gray' },
          { label: 'Pending', value: activity.pending, tone: 'orange' },
        ]}
        footer={byCheck.checking_activity ? `${fmt(byCheck.checking_activity)} checking now` : null}
      />
      <StageCard
        stepNumber={step++}
        title="Stored"
        subtitle="Final dataset"
        accent="emerald"
        bar={{
          label: 'vs raw stored',
          percent: percent(filteredReady, rawUnique),
          barColor: 'bg-gradient-to-r from-emerald-400 to-emerald-500',
        }}
        stats={[{ label: 'Stored', value: filteredReady, tone: 'emerald' }]}
      />
    </div>
  );
}

export function CollapsibleDrawer({ open, onToggle, title, subtitle, children, accent = 'default' }) {
  const accentBorder = accent === 'red'
    ? 'border-red-200 dark:border-red-800/60'
    : 'border-gray-200 dark:border-gray-600';
  const accentBg = accent === 'red'
    ? 'bg-red-50/50 dark:bg-red-950/20 hover:bg-red-50 dark:hover:bg-red-950/30'
    : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700';

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between p-3 rounded-2xl transition-colors border ${accentBorder} ${accentBg}`}
      >
        <div className="flex items-center gap-2 text-left">
          <ChevronDownIcon
            className={`h-5 w-5 text-gray-500 dark:text-gray-400 shrink-0 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`}
          />
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</span>
            {subtitle && <div className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>}
          </div>
        </div>
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          {children}
        </div>
      )}
    </div>
  );
}

function sourceStatusLabel(src) {
  if (isDomainBlocked(src)) {
    if (src.excludeReason === 'no_link') return 'blocked — no link';
    if (src.excludeReason === 'domain_not_approved') return 'blocked — domain';
    if (src.excludeReason === 'deep_link_no_platform') return 'blocked — no platform';
    return 'blocked';
  }
  const status = typeof src === 'string' ? src : src?.status;
  if (status === 'exhausted_dup') return 'dup exit';
  if (status === 'partial') return 'partial';
  if (status === 'excluded') return 'blocked';
  return status;
}

function sourceBarColor(src) {
  const status = typeof src === 'string' ? src : src?.status;
  if (isDomainBlocked(src) || status === 'excluded') return 'bg-rose-400';
  if (status === 'completed') return 'bg-green-500';
  if (status === 'running') return 'bg-indigo-500';
  if (status === 'partial') return 'bg-amber-500';
  if (status === 'exhausted_dup') return 'bg-orange-500';
  return 'bg-gray-400';
}

function sourceBarWidth(src) {
  const { crawlPercent } = sourceCrawlStats(src);
  if (crawlPercent != null) return crawlPercent;
  if (src.status === 'completed') return 100;
  return 0;
}

/** Where the SOURCE ACCOUNT itself is "Based in" — not its followers. */
function SourceCountryBadge({ country, checkedAt, profileUrls }) {
  const firstUrl = Array.isArray(profileUrls) && profileUrls.length > 0 ? profileUrls[0] : null;
  if (!country && !firstUrl) return null;
  const title = checkedAt ? `Checked ${new Date(checkedAt).toLocaleString()}` : undefined;
  return (
    <>
      {country && (
        <span
          title={title}
          className="mt-1 inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 capitalize"
        >
          <GlobeAltIcon className="w-3 h-3 shrink-0" />
          {country}
        </span>
      )}
      {firstUrl && (
        <span
          title={firstUrl}
          className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400"
        >
          <LinkIcon className="w-3 h-3 shrink-0" />
          {firstUrl.length > 40 ? `${firstUrl.slice(0, 40)}…` : firstUrl}
        </span>
      )}
    </>
  );
}

function SourceRowMenu({
  username,
  busy,
  onRunCountryCheck,
  onRunLinkCheck,
  onRemove,
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={Boolean(busy)}
        className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-200 disabled:opacity-40"
        aria-label={`Actions for @${username}`}
      >
        <EllipsisVerticalIcon className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-52 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1 text-left">
            <button
              type="button"
              disabled={busy === 'checking'}
              onClick={() => { setOpen(false); onRunCountryCheck(username); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Checks where this source account itself is based"
            >
              <GlobeAltIcon className="w-3.5 h-3.5" />
              {busy === 'checking' ? 'Checking…' : 'Check source country'}
            </button>
            <button
              type="button"
              disabled={busy === 'link-checking'}
              onClick={() => { setOpen(false); onRunLinkCheck(username); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Fetches the profile link for this source account"
            >
              <LinkIcon className="w-3.5 h-3.5" />
              {busy === 'link-checking' ? 'Checking…' : 'Check source link'}
            </button>
            <button
              type="button"
              disabled={busy === 'removing'}
              onClick={() => { setOpen(false); onRemove(username); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40"
            >
              <TrashIcon className="w-3.5 h-3.5" />
              {busy === 'removing' ? 'Removing…' : 'Remove & blacklist'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const SOURCE_PROGRESS_TABS = [
  { id: 'running', label: 'Running' },
  { id: 'finished', label: 'Finished or stopped' },
  { id: 'blocked', label: 'Blocked' },
];

export function ScraperV2SourceProgressList({
  sourceProgress,
  sessionId,
  accountStats,
  onRunCountryCheck,
  onRemoveSource,
}) {
  const [busy, setBusy] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [including, setIncluding] = useState(false);
  const [tab, setTab] = useState('running');

  const { running, finished, blocked } = useMemo(
    () => partitionSourceProgress(sourceProgress),
    [sourceProgress]
  );

  if (!sourceProgress?.length) return null;

  const visible = tab === 'blocked' ? blocked : tab === 'finished' ? finished : running;
  const emptyCopy = {
    running: 'No sources currently fetching.',
    finished: 'No finished or stopped sources.',
    blocked: 'No domain-blocked sources.',
  };

  const inUse = running.filter((s) => s.status === 'running').length;
  const available = Number(accountStats?.available) || 0;
  const idle = Math.max(0, available - inUse);
  const showAccountLine = accountStats?.total > 0 || inUse > 0;

  const handleRunCountryCheck = async (username) => {
    if (!sessionId) return;
    setBusy((prev) => ({ ...prev, [username]: 'checking' }));
    try {
      const res = await api.post(`/scraper-v2/sessions/${sessionId}/sources/${encodeURIComponent(username)}/country-check`);
      if (res.data.success) {
        toast.success(res.data.message || `@${username} checked`);
        onRunCountryCheck?.(username);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to check country');
    } finally {
      setBusy((prev) => ({ ...prev, [username]: null }));
    }
  };

  const handleRunLinkCheck = async (username) => {
    if (!sessionId) return;
    setBusy((prev) => ({ ...prev, [username]: 'link-checking' }));
    try {
      const res = await api.post(`/scraper-v2/sessions/${sessionId}/sources/${encodeURIComponent(username)}/link-check`);
      if (res.data.success) {
        toast.success(`@${username}: ${res.data.profileUrls?.length ? res.data.profileUrls[0] : 'no links found'}`);
        onRunCountryCheck?.(username);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to check link');
    } finally {
      setBusy((prev) => ({ ...prev, [username]: null }));
    }
  };

  const handleRemove = async (username) => {
    if (!sessionId) return;
    if (!window.confirm(
      `Remove @${username} from this session's Source progress?\n\n`
      + 'The worker on it stops after its current page, and it is blacklisted globally so no session will scrape it again. '
      + 'Data already collected from it is kept.'
    )) {
      return;
    }
    setBusy((prev) => ({ ...prev, [username]: 'removing' }));
    try {
      const res = await api.delete(`/scraper-v2/sessions/${sessionId}/sources/${encodeURIComponent(username)}`);
      if (res.data.success) {
        toast.success(res.data.message || `Removed @${username}`);
        onRemoveSource?.(username);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove source');
      setBusy((prev) => ({ ...prev, [username]: null }));
    }
  };

  const includeSources = async (usernames) => {
    if (!sessionId || !usernames.length || including) return;
    setIncluding(true);
    try {
      const res = await api.post(`/scraper-v2/sessions/${sessionId}/sources/include`, { usernames });
      if (res.data.success) {
        toast.success(res.data.message || `Queued ${usernames.length} for scrape`);
        setSelected(new Set());
        onRunCountryCheck?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to queue sources');
    } finally {
      setIncluding(false);
    }
  };

  const toggleSelected = (username) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username);
      else next.add(username);
      return next;
    });
  };

  const tabCounts = { running: running.length, finished: finished.length, blocked: blocked.length };

  return (
    <div className="space-y-3">
      {showAccountLine && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
          <span>
            Scrape accounts ·{' '}
            <span className="font-semibold tabular-nums text-indigo-600 dark:text-indigo-300">{fmt(inUse)}</span>
            {' '}in use
            {accountStats?.total > 0 && (
              <>
                {' · '}
                <span className="font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{fmt(idle)}</span>
                {' idle of '}
                <span className="tabular-nums">{fmt(available)}</span>
                {' available'}
                {accountStats.rateLimited > 0 ? ` · ${fmt(accountStats.rateLimited)} rate-limited` : ''}
                {accountStats.blocked > 0 ? ` · ${fmt(accountStats.blocked)} blocked` : ''}
              </>
            )}
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {SOURCE_PROGRESS_TABS.map((t) => {
          const active = tab === t.id;
          const count = tabCounts[t.id];
          const blockedTone = t.id === 'blocked';
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-2.5 py-1 rounded-lg border text-xs font-medium ${
                active
                  ? blockedTone
                    ? 'border-rose-500 text-rose-700 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-200'
                    : 'border-indigo-500 text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                  : 'border-gray-300 dark:border-gray-600 text-gray-500 hover:border-gray-400'
              }`}
            >
              {t.label}
              <span className="ml-1 tabular-nums opacity-80">{fmt(count)}</span>
            </button>
          );
        })}
      </div>

      {tab === 'blocked' && blocked.length > 0 && sessionId && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs rounded-lg border border-rose-200/80 dark:border-rose-900/40 bg-rose-50/70 dark:bg-rose-950/20 px-2.5 py-1.5">
          <span className="text-rose-800 dark:text-rose-200">
            {blocked.length} blocked by domain filter
            {selected.size > 0 ? ` · ${selected.size} selected` : ''}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="px-2 py-1 rounded-md border border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-200 hover:bg-rose-100 dark:hover:bg-rose-900/40"
              onClick={() => setSelected(
                selected.size === blocked.length
                  ? new Set()
                  : new Set(blocked.map((s) => s.username))
              )}
            >
              {selected.size === blocked.length ? 'Clear' : 'Select all blocked'}
            </button>
            <button
              type="button"
              disabled={including || selected.size === 0}
              className="px-2 py-1 rounded-md bg-indigo-600 text-white disabled:opacity-40"
              onClick={() => includeSources([...selected])}
            >
              {including ? 'Queuing…' : `Scrape selected`}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3 max-h-80 overflow-y-auto">
        {visible.length === 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400 py-2">{emptyCopy[tab]}</p>
        )}
        {visible.map((src, idx) => (
          <div key={`${src.username}-${idx}`} className="text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <span className="font-mono text-gray-900 dark:text-white inline-flex items-center gap-1.5 min-w-0">
                {isDomainBlocked(src) && sessionId && (
                  <input
                    type="checkbox"
                    checked={selected.has(src.username)}
                    onChange={() => toggleSelected(src.username)}
                    className="rounded border-gray-300 dark:border-gray-600"
                    aria-label={`Select @${src.username}`}
                  />
                )}
                @{src.username}
                <span className={`ml-1 text-xs capitalize ${isDomainBlocked(src) ? 'text-rose-600 dark:text-rose-300' : 'text-gray-500'}`}>
                  {sourceStatusLabel(src)}
                </span>
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">
                  {(() => {
                    const { ingested, fetched, target, crawlPercent, ingestPercent } = sourceCrawlStats(src);
                    const profileNote = isApiEndSource(src) && src.followersCount > fetched
                      ? ` · profile ${fmt(src.followersCount)}`
                      : '';
                    return (
                      <>
                        {fmt(ingested)} unique · {fmt(fetched)} fetched
                        {' / '}
                        {target > 0 ? fmt(target) : '?'}
                        {crawlPercent != null && ` · ${crawlPercent}% crawl`}
                        {ingestPercent != null && ` · ${ingestPercent}% ingest`}
                        {profileNote}
                      </>
                    );
                  })()}
                </span>
                {isDomainBlocked(src) && sessionId && (
                  <button
                    type="button"
                    disabled={including || busy[src.username]}
                    title="Scrape this source anyway — does not approve the domain for others"
                    onClick={() => includeSources([src.username])}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 disabled:opacity-40"
                  >
                    <CheckIcon className="w-3.5 h-3.5" />
                    Scrape
                  </button>
                )}
                {sessionId && (
                  <SourceRowMenu
                    username={src.username}
                    busy={busy[src.username]}
                    onRunCountryCheck={handleRunCountryCheck}
                    onRunLinkCheck={handleRunLinkCheck}
                    onRemove={handleRemove}
                  />
                )}
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${sourceBarColor(src)}`}
                style={{
                  width: `${sourceBarWidth(src)}%`,
                }}
              />
            </div>
            <SourceCountryBadge country={src.country} checkedAt={src.countryCheckedAt} profileUrls={src.profileUrls} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Bulk "check every source account's own country" bar — AboutAccountQuery on
 * each @sourceUsername in this session that doesn't have a cached country
 * yet. Runs in the background (can be thousands of sources); progress lives
 * in statsCache.sourceCountryBackfill.
 */
export function ScraperV2SourceCountryCheckBar({ sessionId, backfill, onStarted }) {
  const [starting, setStarting] = useState(false);

  if (!sessionId) return null;

  const status = backfill?.status;
  const busy = starting || status === 'running';

  const start = async () => {
    setStarting(true);
    try {
      const res = await api.post(`/scraper-v2/sessions/${sessionId}/sources-country-check`);
      if (res.data.success) {
        toast(res.data.message || 'Checking source account countries…', { icon: '🌍' });
        onStarted?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start check');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3 text-xs">
      <span className="text-gray-500 dark:text-gray-400">
        {status === 'running' && `Checking source countries… ${fmt(backfill.filled || 0)}/${fmt(backfill.attempted || 0)} done`}
        {status === 'done' && `Last run: found a country for ${fmt(backfill.filled || 0)}/${fmt(backfill.attempted || 0)} checked`}
        {!status && "Where each source account is \"Based in\" — not its followers"}
      </span>
      <button
        type="button"
        onClick={start}
        disabled={busy}
        className="px-2.5 py-1 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 shrink-0"
      >
        {busy ? 'Checking…' : 'Check source countries'}
      </button>
    </div>
  );
}

/**
 * Bulk "check every source account's own profile links" bar — UserByScreenName
 * on each @sourceUsername in this session that doesn't have cached links yet.
 * Runs in the background; progress lives in statsCache.sourceLinkBackfill.
 */
export function ScraperV2SourceLinkCheckBar({ sessionId, backfill, onStarted }) {
  const [starting, setStarting] = useState(false);
  if (!sessionId) return null;

  const status = backfill?.status;
  const busy = starting || status === 'running';

  const start = async () => {
    setStarting(true);
    try {
      const res = await api.post(`/scraper-v2/sessions/${sessionId}/sources-link-check`);
      if (res.data.success) {
        toast(res.data.message || 'Checking source account links…', { icon: '🔗' });
        onStarted?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start link check');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3 text-xs">
      <span className="text-gray-500 dark:text-gray-400">
        {status === 'running' && `Checking source links… ${fmt(backfill.filled || 0)}/${fmt(backfill.attempted || 0)} done`}
        {status === 'done' && `Last run: found links for ${fmt(backfill.filled || 0)}/${fmt(backfill.attempted || 0)} checked`}
        {!status && 'Profile link (website / bio URL) for each source account'}
      </span>
      <button
        type="button"
        onClick={start}
        disabled={busy}
        className="px-2.5 py-1 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 shrink-0"
      >
        {busy ? 'Checking…' : 'Check links'}
      </button>
    </div>
  );
}

/**
 * Editable "accepted countries" list for a session that already exists —
 * the create-session modal only lets you set this once. The live crawl
 * already runs AboutAccountQuery against this list for newly-discovered
 * followers; this panel just lets you grow the whitelist mid-session.
 */
export function ScraperV2CountryFilterPanel({ sessionId, filters, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [addInput, setAddInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [gateToggling, setGateToggling] = useState(false);

  const allowedCountries = filters?.allowedCountries || [];
  const autoSourceCountryGate = Boolean(filters?.autoSourceCountryGate);

  const toggleAutoSourceGate = async () => {
    if (!sessionId || gateToggling) return;
    const next = !autoSourceCountryGate;
    setGateToggling(true);
    try {
      const res = await api.patch(`/scraper-v2/sessions/${sessionId}/filters`, {
        autoSourceCountryGate: next,
      });
      if (res.data.success) {
        toast.success(
          next
            ? 'Auto-sourcing from FYP will now check each new username\u2019s own country first'
            : 'Auto-sourcing from FYP no longer checks country'
        );
        onUpdated?.(res.data.filters);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update the toggle');
    } finally {
      setGateToggling(false);
    }
  };

  const startEdit = () => {
    setDraft([...allowedCountries]);
    setAddInput('');
    setEditing(true);
  };

  const addCountry = () => {
    const t = addInput.trim();
    if (!t) return;
    if (!draft.some((c) => c.toLowerCase() === t.toLowerCase())) {
      setDraft([...draft, t]);
    }
    setAddInput('');
  };

  const save = async () => {
    if (!sessionId) return;
    setSaving(true);
    try {
      const res = await api.patch(`/scraper-v2/sessions/${sessionId}/filters`, {
        allowedCountries: draft,
        filterCountryWhitelist: draft.length > 0,
      });
      if (res.data.success) {
        toast.success(`Saved ${draft.length} accepted countr${draft.length === 1 ? 'y' : 'ies'}`);
        setEditing(false);
        onUpdated?.(res.data.filters);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save countries');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 dark:bg-indigo-950/20 p-4">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-800 dark:text-indigo-300">
            Accepted countries
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
            AboutAccountQuery checks each username&apos;s &quot;Based in&quot; country against this list.
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={startEdit}
            className="px-2.5 py-1 text-xs rounded-lg border border-gray-300 dark:border-gray-600 shrink-0"
          >
            Edit
          </button>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 mb-3 p-2.5 rounded-lg bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
        <div className="min-w-0">
          <div className="text-xs font-medium text-gray-800 dark:text-gray-200">
            Gate auto-sourcing from FYP by country
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            Before a new username discovered from FYP is added as a source account, check its own
            &quot;Based in&quot; country and only add it if it&apos;s on the accepted list above. Live — takes
            effect on the next sweep.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={autoSourceCountryGate}
          onClick={toggleAutoSourceGate}
          disabled={gateToggling || !sessionId}
          className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${
            autoSourceCountryGate ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              autoSourceCountryGate ? 'translate-x-[18px]' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      {!editing ? (
        allowedCountries.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {allowedCountries.map((c) => (
              <span
                key={c}
                className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200 capitalize"
              >
                {c}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No accepted countries yet — click Edit to add some.
          </p>
        )
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 rounded-md bg-white/60 dark:bg-gray-800/60 border dark:border-gray-700">
            {draft.length === 0 && <span className="text-xs text-gray-400">No countries yet</span>}
            {draft.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200"
              >
                {c}
                <button
                  type="button"
                  onClick={() => setDraft(draft.filter((x) => x !== c))}
                  className="hover:text-red-600 font-semibold"
                  aria-label={`Remove ${c}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={addInput}
              onChange={(e) => setAddInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCountry();
                }
              }}
              placeholder="Add country (spelling as on X; any case)"
              className="flex-1 min-w-[180px] rounded-md border px-2 py-1.5 text-sm dark:bg-gray-800 dark:border-gray-700"
            />
            <button
              type="button"
              onClick={addCountry}
              className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Add
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={save}
              className="px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="px-3 py-1.5 text-sm rounded-md border dark:border-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ScraperV2AddSourcesPanel({
  sessionId,
  sessionStatus,
  categories = [],
  onAdded,
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [tag, setTag] = useState('');
  const [newTag, setNewTag] = useState('');
  const [mode, setMode] = useState('select'); // select | create
  const [submitting, setSubmitting] = useState(false);

  if (!['running', 'paused', 'stopped'].includes(sessionStatus)) return null;

  const resolvedTag = mode === 'create' ? newTag.trim() : tag.trim();

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!sessionId || !value.trim() || !resolvedTag) return;
    setSubmitting(true);
    try {
      const usernames = value
        .split(/[\n,]+/)
        .map((u) => u.trim())
        .filter(Boolean);
      const res = await api.post(
        `/scraper-v2/sessions/${encodeURIComponent(sessionId)}/add-usernames`,
        { usernames, tag: resolvedTag }
      );
      if (res.data.success) {
        const { added = 0, skipped = 0, skippedGlobal = 0, tag: usedTag } = res.data;
        if (added > 0) {
          toast.success(
            `Added ${added} source(s) under "${usedTag || resolvedTag}"`
            + (skipped || skippedGlobal
              ? ` · skipped ${skipped + skippedGlobal}`
              : '')
          );
        } else {
          toast(
            res.data.message
              || `No new usernames (skipped ${skipped + skippedGlobal})`,
            { icon: 'ℹ️' }
          );
        }
        setValue('');
        setOpen(false);
        onAdded?.(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add usernames');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-xl"
      >
        Add source accounts
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <form
            onSubmit={handleSubmit}
            className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-lg w-full p-5 space-y-4 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Add source accounts
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Pick a category for this batch. Already-seen usernames (this session or global history) are skipped.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-sm"
              >
                Close
              </button>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Category
              </label>
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setMode('select')}
                  className={`px-2.5 py-1 rounded-lg border ${
                    mode === 'select'
                      ? 'border-indigo-500 text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                      : 'border-gray-300 dark:border-gray-600 text-gray-500'
                  }`}
                >
                  Existing
                </button>
                <button
                  type="button"
                  onClick={() => setMode('create')}
                  className={`px-2.5 py-1 rounded-lg border ${
                    mode === 'create'
                      ? 'border-indigo-500 text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                      : 'border-gray-300 dark:border-gray-600 text-gray-500'
                  }`}
                >
                  New category
                </button>
              </div>
              {mode === 'select' ? (
                <select
                  required
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                >
                  <option value="">Select category…</option>
                  {categories.map((c) => (
                    <option key={c.id || c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  required
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="e.g. Gaming"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                />
              )}
            </div>

            <textarea
              required
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={'Enter usernames or URLs (one per line)\ne.g. username, @user, https://x.com/user'}
              rows={6}
              className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 font-mono resize-y"
            />

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !value.trim() || !resolvedTag}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg"
              >
                {submitting ? 'Adding…' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
